import type { TieTeam } from "../lib/tie.js";
import type { Roster } from "../routes/event/shared.js";
import { Avatar } from "./Avatar.js";

/**
 * The two team sheets of a tie, side by side, in seed order.
 *
 * Read-only and shown to everybody. A team sheet is the least secret thing at a tournament,
 * and a player wants to know which side they are on well before a draw exists.
 */
export function TeamSheets({ teams, roster }: { teams: TieTeam[]; roster: Roster }) {
  const sideA = teams.find((team) => team.side === "A");
  const sideB = teams.find((team) => team.side === "B");
  if (!sideA?.members.length || !sideB?.members.length) return null;

  return (
    <div className="flex gap-6">
      <TeamSheet team={sideA} roster={roster} />
      <TeamSheet team={sideB} roster={roster} />
    </div>
  );
}

function TeamSheet({ team, roster }: { team: TieTeam; roster: Roster }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="micro">
        {team.name} · {team.members.length}
      </p>
      <ol className="mt-2">
        {team.members.map((member) => {
          // Absent from the entry list means they withdrew after the sheet was written. The
          // draw refuses that state; showing it is how the organiser finds out.
          const person = roster.get(member.memberId) ?? { name: "withdrawn", url: null };

          return (
            <li
              key={member.memberId}
              className="flex items-center gap-2 border-t py-1.5"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <span className="micro">{member.seed}</span>
              <Avatar name={person.name} url={person.url} size={22} />
              <span className="truncate text-sm">{person.name}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
